// Appels HTTP typés vers /api/invoices/* — liste filtrée, détail, notes, paiements.

import { api } from "./client";
import type {
  InvoiceDetail,
  InvoiceFilters,
  InvoiceListResponse,
  InvoicePublic,
  InvoiceUpdatePayload,
  PaymentCreatePayload,
  PaymentLinkPublic,
  PaymentPublic,
  ReminderPublic,
  UUID,
} from "./types";

function buildQuery(filters: InvoiceFilters): string {
  const parts: string[] = [];
  const push = (k: string, v: string | number | boolean) =>
    parts.push(`${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`);

  if (filters.status) {
    for (const s of filters.status) push("status", s);
  }
  if (filters.client_id) push("client_id", filters.client_id);
  if (filters.project_id) push("project_id", filters.project_id);
  if (filters.intervenant_id) push("intervenant_id", filters.intervenant_id);
  if (filters.issue_date_from) push("issue_date_from", filters.issue_date_from);
  if (filters.issue_date_to) push("issue_date_to", filters.issue_date_to);
  if (filters.q) push("q", filters.q);
  if (filters.overdue_only) push("overdue_only", "true");
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  if ((filters as any).invoice_type) push("invoice_type", (filters as any).invoice_type);
  if (filters.limit !== undefined) push("limit", filters.limit);
  if (filters.offset !== undefined) push("offset", filters.offset);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  if ((filters as any).currency) push("currency", (filters as any).currency);

  return parts.length ? `?${parts.join("&")}` : "";
}

export const invoicesApi = {
  list: (filters: InvoiceFilters = {}) =>
    api.get<InvoiceListResponse>(`/api/invoices${buildQuery(filters)}`),

  get: (id: UUID) => api.get<InvoiceDetail>(`/api/invoices/${id}`),

  patch: (id: UUID, payload: InvoiceUpdatePayload) =>
    api.patch<InvoicePublic>(`/api/invoices/${id}`, payload),

  addPayment: (id: UUID, payload: PaymentCreatePayload) =>
    api.post<PaymentPublic>(`/api/invoices/${id}/payments`, payload),

  /**
   * Génère (ou régénère) un lien de paiement Ziina pour la facture (Lot 5.2).
   *
   * Le backend écrase la dernière version stockée sur l'invoice — on ne garde
   * pas d'historique des liens abandonnés. Retourne la forme publique du lien ;
   * pour refléter dans l'UI, l'appelant peut merger les champs `payment_link_*`
   * sur l'invoice locale ou re-fetch le détail.
   */
  createPaymentLink: (id: UUID) =>
    api.post<PaymentLinkPublic>(`/api/invoices/${id}/payment-link`, {}),

  /**
   * Historique des relances pour une facture (Lot 4A).
   *
   * Le détail de la facture les inclut déjà dans `invoice.reminders`, mais
   * ce helper permet de re-fetch sans recharger tout le détail si on veut
   * refresh seulement cette section (ex : après un envoi manuel au Lot 4B).
   */
  getReminders: (id: UUID) =>
    api.get<ReminderPublic[]>(`/api/invoices/${id}/reminders`),

  /**
   * Télécharge l'export du portefeuille filtré dans le format demandé.
   * On ne passe pas la pagination (limit/offset) — l'export doit être
   * complet sur le périmètre filtré.
   */
  delete: (id: UUID) => api.delete<void>(`/api/invoices/${id}`),

  /**
   * Télécharge le PDF généré dynamiquement pour une facture.
   */
  downloadPdf: (id: UUID, lang: "fr" | "en" = "fr") =>
    api.getBlob(`/api/invoices/${id}/pdf?lang=${lang}`),

  exportAs: (format: "csv" | "xlsx", filters: InvoiceFilters = {}) => {
    // L'export est toujours sur le portefeuille complet filtré — on ignore
    // la pagination courante de la liste.
    const exportFilters: InvoiceFilters = {
      ...filters,
      limit: undefined,
      offset: undefined,
    };
    const path = `/api/invoices/export.${format}${buildQuery(exportFilters)}`;
    return api.getBlob(path);
  },
};
